import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";

export type EmailOtpLabels = {
  stepHint?: string;
  title?: string;
  description?: string;
  codeLabel?: string;
  expiresIn?: string;
  expired?: string;
  resend?: string;
  resendIn?: string;
  sending?: string;
  continue?: string;
  continuing?: string;
};

interface EmailOtpStepProps {
  email: string;
  otpCode: string;
  onOtpCodeChange: (code: string) => void;
  onVerify: (code: string) => void;
  onResend: () => void;
  onBack?: () => void;
  backLabel?: string;
  otpSending: boolean;
  otpVerifying: boolean;
  otpSecondsLeft: number;
  otpExpiresAtMs: number | null;
  resendCooldown: number;
  continueBusy?: boolean;
  labels: EmailOtpLabels;
  formatMmSs: (totalSeconds: number) => string;
}

const EmailOtpStep = ({
  email,
  otpCode,
  onOtpCodeChange,
  onVerify,
  onResend,
  onBack,
  backLabel = "Tillbaka",
  otpSending,
  otpVerifying,
  otpSecondsLeft,
  otpExpiresAtMs,
  resendCooldown,
  continueBusy = false,
  labels,
  formatMmSs,
}: EmailOtpStepProps) => {
  const busy = otpSending || otpVerifying || continueBusy;
  const expired = otpExpiresAtMs !== null && otpSecondsLeft <= 0;

  return (
    <div className="mx-auto w-full max-w-lg space-y-5">
      <div className="flex items-center gap-2">
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={onBack}
            aria-label={backLabel}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        ) : null}
        <div className="min-w-0 space-y-0.5">
          {labels.stepHint ? (
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {labels.stepHint}
            </p>
          ) : null}
          <h3 className="text-xl font-semibold tracking-tight leading-tight">
            {labels.title || "Verifiera e-post"}
          </h3>
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-muted/30 px-4 py-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background border border-border">
            <Mail className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-muted-foreground leading-snug">
              {(labels.description || "Vi har skickat en 6-siffrig kod till {email}.").replace(
                "{email}",
                email,
              )}
            </p>
            {otpSecondsLeft > 0 ? (
              <p className="text-xs font-medium text-foreground/80">
                {(labels.expiresIn || "Koden gäller i {time}").replace(
                  "{time}",
                  formatMmSs(otpSecondsLeft),
                )}
              </p>
            ) : otpExpiresAtMs ? (
              <p className="text-xs font-medium text-destructive">
                {labels.expired || "Koden har gått ut. Skicka en ny kod."}
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {labels.codeLabel || "Verifieringskod"}
          </Label>
          <InputOTP
            maxLength={6}
            value={otpCode}
            onChange={(value) => {
              const next = value.replace(/\D/g, "").slice(0, 6);
              onOtpCodeChange(next);
              if (next.length === 6 && !busy && !expired) {
                onVerify(next);
              }
            }}
            containerClassName="justify-center gap-2"
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: 6 }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className="h-11 w-9 sm:h-12 sm:w-10 text-base"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground shrink-0 order-2 sm:order-1"
          disabled={busy || resendCooldown > 0}
          onClick={onResend}
        >
          {otpSending
            ? labels.sending || "Skickar..."
            : resendCooldown > 0
              ? (labels.resendIn || "Skicka igen om {time}").replace(
                  "{time}",
                  formatMmSs(resendCooldown),
                )
              : labels.resend || "Skicka igen"}
        </Button>
        <Button
          type="button"
          className="w-full sm:w-auto shrink-0 order-1 sm:order-2"
          disabled={otpCode.length !== 6 || busy || expired}
          onClick={() => onVerify(otpCode)}
        >
          {otpVerifying || continueBusy ? (
            labels.continuing || "Fortsätter..."
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">{labels.continue || "Verifiera"}</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EmailOtpStep;
